import { Fragment, useEffect, useRef, useState } from 'react';
import { gallery } from './content/gallery';
import type { MediaAsset } from './types/content';
import './styles/editorial.css';

const byId = (id: string) => gallery.find((item) => item.id === id)!;
const heroFrames = ['hero-video-01', 'hero-video-02', 'hero-video-03', 'hero-video-04'].map(byId);
const heroCaptions = ['ВИДЕОПОРТРЕТ', 'УЛИЧНАЯ МОДА', 'ЗИМНИЙ КИБОРГ', 'БОЕВОЙ КИБОРГ'];
const viewingChapters = [
  { id: 'top', label: 'Обложка' },
  { id: 'scroll-story', label: 'Манифест' },
  { id: 'work', label: 'Избранное' },
  { id: 'formats', label: 'Форматы услуг' },
  { id: 'process', label: 'Процесс' },
  { id: 'expert-videos', label: 'Экспертные ролики' },
  { id: 'learning-materials', label: 'Обучающие материалы' },
  { id: 'faq', label: 'Вопросы' },
  { id: 'about', label: 'Обо мне' },
];
const coffeeGenerationVideo: MediaAsset = {
  id: 'coffee-generation',
  title: 'Кофе — рекламный ролик',
  type: 'video' as const,
  src: '/media/denis/coffee-generation-web.mp4',
  poster: '/media/denis/coffee-generation-web-poster.jpg',
  alt: 'Кофейный напиток и упаковка кофе в рекламной видеосцене',
  aspectRatio: 9 / 16,
  description: 'Рекламный ролик кофе · продукт и атмосфера',
  status: 'real',
};
const homeCharacterVideos: MediaAsset[] = [
  'home-character-01',
  'home-character-02',
  'home-character-03',
  'home-character-04',
  'home-character-05',
  'home-character-06',
].map((id, index) => ({
  id,
  title: `Видео ${String(index + 1).padStart(2, '0')} — персонаж`,
  type: 'video' as const,
  src: `/media/denis/${id}.mp4`,
  poster: `/media/denis/${id}-poster.jpg`,
  alt: `Видеоматериал ${index + 1} для проекта «Персонаж как целый мир»`,
  aspectRatio: 9 / 16,
  description: 'Видеоматериал · проект «Персонаж как целый мир»',
  status: 'real' as const,
}));
const homeFurnitureVideo: MediaAsset = {
  id: 'home-short-04',
  title: 'Дом и мебель — короткий ролик',
  type: 'video',
  src: '/media/denis/home-short-04.mp4',
  poster: '/media/denis/home-short-04-poster.jpg',
  alt: 'Интерьер и мебель появляются в коротком видеоролике',
  aspectRatio: 16 / 9,
  description: 'Видео с ИИ · интерьер и предметный дизайн',
  status: 'real',
};
const homeFashionVideo: MediaAsset = {
  id: 'home-short-05',
  title: 'Модная реклама — короткий ролик',
  type: 'video',
  src: '/media/denis/home-short-05.mp4',
  poster: '/media/denis/home-short-05-poster.jpg',
  alt: 'Короткий видеоролик модной рекламной съёмки',
  aspectRatio: 9 / 16,
  description: 'Реклама моды · короткий видеоролик',
  status: 'real',
};
const homeCoffeeVideo: MediaAsset = {
  id: 'home-short-06',
  title: 'Кофе — рекламный ролик',
  type: 'video',
  src: '/media/denis/home-short-06.mp4',
  poster: '/media/denis/home-short-06-poster.jpg',
  alt: 'Короткий рекламный видеоролик кофе',
  aspectRatio: 16 / 9,
  description: 'Реклама кофе · короткий видеоролик',
  status: 'real',
};
const expertClips: ExpertClip[] = [
  { id: 'expert-canva', title: 'Canva и нейросети', topic: 'ИНСТРУМЕНТЫ', src: '/media/denis/expert/expert-canva.mp4', poster: '/media/denis/expert/expert-canva-poster.jpg' },
  { id: 'expert-storyboard', title: 'Скелет раскадровки', topic: 'СЦЕНАРИЙ', src: '/media/denis/expert/expert-storyboard.mp4', poster: '/media/denis/expert/expert-storyboard-poster.jpg' },
  { id: 'expert-generation-volume', title: 'Объём генерации', topic: 'AI-ПРОИЗВОДСТВО', src: '/media/denis/expert/expert-generation-volume.mp4', poster: '/media/denis/expert/expert-generation-volume-poster.jpg' },
  { id: 'expert-photo-commands', title: 'Команды GPT для фото', topic: 'AI-ИЗОБРАЖЕНИЯ', src: '/media/denis/expert/expert-photo-commands.mp4', poster: '/media/denis/expert/expert-photo-commands-poster.jpg' },
  { id: 'expert-tashkent', title: 'Ташкент глазами нейросети', topic: 'ВИЗУАЛЬНЫЙ КОНЦЕПТ', src: '/media/denis/expert/expert-tashkent.mp4', poster: '/media/denis/expert/expert-tashkent-poster.jpg' },
  { id: 'expert-ai-guide', title: 'Гайд по нейросетям', topic: 'ОБЗОР ИНСТРУМЕНТОВ', src: '/media/denis/expert/expert-ai-guide-final.mp4', poster: '/media/denis/expert/expert-ai-guide-poster.jpg' },
  { id: 'expert-red-bull', title: 'Концепт Red Bull', topic: 'РЕКЛАМНЫЙ КОНЦЕПТ', src: '/media/denis/expert/expert-red-bull.mp4', poster: '/media/denis/expert/expert-red-bull-poster.jpg' },
  { id: 'expert-jewelry-ai', title: 'AI-сцена из трёх картинок', topic: 'AI-ПРОИЗВОДСТВО', src: '/media/denis/expert/expert-jewelry-ai.mp4', poster: '/media/denis/expert/expert-jewelry-ai-poster.jpg' },
  { id: 'expert-map-story', title: 'Оживить скриншот карты', topic: 'AI-ВИЗУАЛЬНЫЙ ПРОЦЕСС', src: '/media/denis/expert/expert-map-story.mp4', poster: '/media/denis/expert/expert-map-story-poster.jpg' },
  { id: 'expert-gpt-shortcuts', title: 'Читерские коды ChatGPT', topic: 'CHATGPT', src: '/media/denis/expert/expert-gpt-shortcuts.mp4', poster: '/media/denis/expert/expert-gpt-shortcuts-poster.jpg' },
];
const learningGuides = [
  { category: 'AI-ВИДЕО', title: 'Фото ожило? Четыре примера', description: 'Исходники, результаты, промпты и пошаговая схема.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover-video-prompts.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/video-prompts/' },
  { category: 'NVIDIA BUILD', title: 'Подключить модели к Claude Code', description: 'Ключ NVIDIA, Router и проверка первого запроса.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover-nvidia.svg', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/nvidia/' },
  { category: 'GOOGLE AI', title: 'Выбрать AI-инструмент под задачу', description: 'Девять сервисов, быстрый старт и заметки об Opal и Mixboard.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/google-tools/cover.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/google-tools/' },
  { category: 'AI ДЛЯ КОНТЕНТА', title: 'Пять задач, которые можно делегировать AI', description: 'Визуал, монтаж, звук и сценарий — с твоей идеей и контролем.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/reels-5-ways-cover.png', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/ai-5-ways/' },
  { category: 'ГЕНЕРАЦИИ', title: 'Четыре вывода после сотни попыток', description: 'Бюджет, персонаж, локация и точность промпта.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/generation/' },
  { category: 'CHATGPT', title: 'Проверить идею с пяти сторон', description: 'Пять режимов диалога: от критики до проверки фактов.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover-five-modes.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/five-modes/' },
  { category: 'AI-ИЗОБРАЖЕНИЯ', title: 'Собрать свет, камеру и оптику', description: 'Готовые связки для портрета, интерьера и киношного кадра.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover-frame-formulas.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/frame-formulas/' },
  { category: 'AI-ВИДЕО', title: 'Разложить действие на четыре кадра', description: 'Шаблон раскадровки 2×2 и схема непрерывного движения.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover-storyboard.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/storyboard/' },
  { category: 'CHATGPT + CANVA', title: 'Превратить изображение в дизайн', description: 'Генерация в ChatGPT, перенос в Canva и редактирование.', cover: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/assets/cover-canva.webp', href: 'https://denis-ai-generation-guide.arhandez.chatgpt.site/canva/' },
];
const homepageDetailVideoOverrides = new Map([...homeCharacterVideos, coffeeGenerationVideo, homeFurnitureVideo, homeFashionVideo, homeCoffeeVideo].map((item) => [item.id, item]));
const projects = [
  {
    number: '01',
    eyebrow: 'Рекламные концепции · видео с ИИ',
    title: 'Идеи брендов\nв движении',
    description: 'Короткие ролики для брендов, моды и больших сюжетов — от выразительного первого кадра до цельной рекламной сцены.',
    format: 'AI-рекламный ролик',
    focus: 'Концепция · сцены · динамика',
    cover: byId('fashion-dynamics'),
    details: ['energy-drink-motion', 'coffee-generation', 'construction-film', 'motorcycle-film', 'fashion-impact', 'chameleon-sneakers'],
  },
  {
    number: '02',
    eyebrow: 'Персонажи · визуальные миры',
    title: 'Персонаж как\nцелый мир',
    description: 'Образы, костюмы и атмосфера — от первого силуэта до выразительной серии концепт-кадров.',
    format: 'Серия визуальных материалов',
    focus: 'Персонаж · костюм · среда',
    cover: byId('editorial-video-main'),
    details: ['home-character-01', 'home-character-02', 'home-character-03', 'home-character-04', 'home-character-05', 'home-character-06'],
  },
  {
    number: '03',
    eyebrow: 'Продукт · рекламные кампании',
    title: 'Свет, материал,\nдеталь',
    description: 'Предметная и модная подача через цвет, свет и фактуру — для визуальных концепций кампаний.',
    format: 'Продуктовая визуальная серия',
    focus: 'Свет · материал · детали',
    cover: byId('jewelry-campaign-video'),
    details: ['jewelry-video-0210', 'jewelry-video-0225', 'jewelry-video-0227'],
  },
  {
    number: '04',
    eyebrow: 'Видео с ИИ · короткий формат',
    title: 'Идея в движении',
    description: 'Короткие видеоэксперименты и визуальные последовательности для продуктовых и сюжетных историй.',
    format: 'Короткий AI-ролик',
    focus: 'Продуктовая или сюжетная сцена',
    cover: byId('perfume'),
    details: ['perfume-rain', 'motion-transfer', 'computer-mouse', 'home-short-04', 'home-short-05', 'home-short-06'],
  },
];
const scrollFilmFrames = [
  ...heroFrames.map((item) => ({ src: item.poster ?? item.src, alt: item.alt })),
  ...projects.map((project) => ({ src: project.cover.poster ?? project.cover.src, alt: project.cover.alt })),
  ...expertClips.map((clip) => ({ src: clip.poster, alt: clip.title })),
];
const storyScenes = [
  { eyebrow: '01 · НАЧИНАЕМ С ОБРАЗА', title: 'Собираю\nсильный образ.', description: 'Подбираю героя, стиль и визуальный язык под задачу.', tone: '#e49a54' },
  { eyebrow: '02 · ДОБАВЛЯЕМ ДВИЖЕНИЕ', title: 'Вдыхаю в него\nдвижение.', description: 'Свет, сцены и детали складываются в цельную историю.', tone: '#b86f9f' },
  { eyebrow: '03 · СОБИРАЕМ ИСТОРИЮ', title: 'Создаю ролик,\nкоторый цепляет.', description: 'От первого кадра до готового материала для кампании.', tone: '#67a9ad' },
];

function contextForItem(id: string) {
  if (id.startsWith('mask02-') || id.startsWith('red-mask-character-') || id === 'mask-02-video') return 'Редакционный проект · SOUL MAGAZINE';
  if (id.startsWith('denis-') || id === 'car-portrait' || id === 'neon-car-portrait' || id === 'hero-video-01' || id === 'vintage-car-portrait-video') return 'Личная съёмка';
  if (id.startsWith('jewelry-')) return 'Концепт рекламной кампании';
  if (id.startsWith('workwear-') || id.startsWith('orange-fashion-') || id === 'energy-drink-motion' || id === 'fashion-dynamics' || id === 'fashion-impact' || id === 'chameleon-sneakers') return 'Рекламный концепт';
  if (id.startsWith('presentation-') || id === 'motion-transfer') return 'Видеоэксперимент';
  if (id.startsWith('soul-mask')) return 'Визуальные исследования · SOUL MAGAZINE';
  return 'Визуальный концепт';
}

function AutoplayVideo({ item, className = '' }: { item: MediaAsset; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window)) {
      if (!reducedMotion) video.play().catch(() => {});
      return () => video.pause();
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) {
        if (!reducedMotion) video.play().catch(() => {});
      } else {
        video.pause();
      }
    }, { rootMargin: '80px', threshold: 0.01 });

    observer.observe(video);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [item.src]);

  return <video ref={videoRef} className={className} src={item.src} poster={item.poster} aria-label={item.alt} muted loop playsInline preload="none" onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }} />;
}

type ExpertClip = { id: string; title: string; topic: string; src: string; poster: string };

function ExpertVideoCard({ clip, index }: { clip: ExpertClip; index: number }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const soundEnabledRef = useRef(false);
  const [withSound, setWithSound] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const startVideo = () => {
      video.muted = !soundEnabledRef.current;
      video.volume = 0.5;
      video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    };
    const onCanPlay = () => startVideo();
    if (!('IntersectionObserver' in window)) {
      startVideo();
      return () => video.pause();
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) {
        video.addEventListener('canplay', onCanPlay, { once: true });
        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) startVideo();
        else video.load();
      } else {
        video.pause();
        setPlaying(false);
      }
    }, { rootMargin: '120px', threshold: 0.15 });

    observer.observe(video);
    return () => {
      observer.disconnect();
      video.removeEventListener('canplay', onCanPlay);
      video.pause();
    };
  }, [clip.src]);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = 0.5;
    soundEnabledRef.current = !withSound;
    video.muted = !soundEnabledRef.current;
    video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    setWithSound(!withSound);
  };

  const retryPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    setFailed(false);
    video.muted = true;
    video.load();
    video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  return <article className="expert-video-card" data-reveal>
    <div className={`expert-video-frame${playing ? ' is-playing' : ''}`}>
      <video ref={videoRef} src={clip.src} poster={clip.poster} aria-label={clip.title} muted loop playsInline preload="none" onLoadedMetadata={(event) => { event.currentTarget.volume = 0.5; }} onError={() => { setFailed(true); setPlaying(false); }} onPause={() => setPlaying(false)} onPlaying={() => { setFailed(false); setPlaying(true); }} />
      {!playing && <button className="expert-video-start" type="button" onClick={retryPlayback} aria-label={`Воспроизвести: ${clip.title}`}><span aria-hidden="true">▶</span></button>}
      <button className="expert-video-sound" type="button" onClick={toggleSound} aria-label={withSound ? `Выключить звук: ${clip.title}` : `Включить звук: ${clip.title}`} aria-pressed={withSound}>
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 5 6 9H3v6h3l5 4V5Z" />
          {withSound ? <><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" /></> : <path d="m16 9 5 6m0-6-5 6" />}
        </svg>
      </button>
      <span className="expert-video-number">{String(index + 1).padStart(2, '0')}</span>
      {failed && <span className="expert-video-error">Видео не загрузилось. Попробуйте обновить страницу.</span>}
    </div>
    <div className="expert-video-copy"><p className="eyebrow">{clip.topic}</p><h3>{clip.title}</h3></div>
  </article>;
}

function AssetImage({ item, eager = false, className = '' }: { item: MediaAsset; eager?: boolean; className?: string }) {
  if (item.type === 'video') {
    return <AutoplayVideo item={item} className={className} />;
  }
  return (
    <img
      className={className}
      src={item.src}
      alt={item.alt}
      loading={eager ? 'eager' : 'lazy'}
      onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
    />
  );
}

function App() {
  const [activeItem, setActiveItem] = useState<MediaAsset | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactTopic, setContactTopic] = useState<string | null>(null);
  const contactDialogRef = useRef<HTMLElement | null>(null);
  const contactCloseRef = useRef<HTMLButtonElement | null>(null);
  const contactTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [activeChapter, setActiveChapter] = useState('top');
  const [storyStep, setStoryStep] = useState(0);
  const heroTouchStart = useRef<number | null>(null);
  const heroItem = heroFrames[heroIndex];
  const showHeroFrame = (direction: number) => setHeroIndex((current) => (current + direction + heroFrames.length) % heroFrames.length);
  const openContact = (trigger: HTMLButtonElement, topic?: string) => {
    contactTriggerRef.current = trigger;
    setContactTopic(topic ?? null);
    setContactOpen(true);
  };
  const closeContact = () => setContactOpen(false);

  useEffect(() => {
    const root = document.documentElement;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const site = document.querySelector('.editorial-site');
    if (!site) return;
    if (!reducedMotion && 'IntersectionObserver' in window) {
      root.classList.add('has-scroll-reveal');
      const revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -36px 0px' });
      const observeReveals = (container: ParentNode) => {
        if (container instanceof Element && container.matches('[data-reveal]') && !container.classList.contains('is-revealed')) revealObserver.observe(container);
        container.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((element) => revealObserver.observe(element));
      };
      observeReveals(site);
      const mutationObserver = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node instanceof Element) observeReveals(node);
      })));
      mutationObserver.observe(site, { childList: true, subtree: true });
      return () => {
        mutationObserver.disconnect();
        revealObserver.disconnect();
        root.classList.remove('has-scroll-reveal');
      };
    }
  }, []);

  useEffect(() => {
    const sections = viewingChapters.map(({ id }) => document.getElementById(id)).filter((element): element is HTMLElement => Boolean(element));
    const site = document.querySelector<HTMLElement>('.editorial-site');
    const story = document.getElementById('scroll-story');
    const sceneTones: Record<string, string> = {
      top: '#e8b18b', work: '#8bb8bd', 'expert-videos': '#d96b58', 'learning-materials': '#a1a76e', formats: '#d39a9a', process: '#78a58d', faq: '#929fc3', about: '#d0b18c',
    };
    let frame = 0;
    const updateChapter = () => {
      frame = 0;
      const marker = window.innerHeight * 0.38;
      const active = sections.find((section) => {
        const bounds = section.getBoundingClientRect();
        return bounds.top <= marker && bounds.bottom > marker;
      });
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const pageProgress = maxScroll > 0 ? Math.min(1, Math.max(0, window.scrollY / maxScroll)) : 0;
      const activeBounds = active?.getBoundingClientRect();
      const chapterProgress = activeBounds ? Math.min(1, Math.max(0, (marker - activeBounds.top) / Math.max(1, activeBounds.height))) : 0;
      const motionScale = window.innerWidth <= 900 ? 0.42 : 1;
      const horizontalDrift = Math.round((Math.sin(pageProgress * Math.PI * 2) * 132 + (chapterProgress - 0.5) * 96) * motionScale);
      const verticalDrift = Math.round((Math.cos(pageProgress * Math.PI * 2) * 78 + (chapterProgress - 0.5) * 102) * motionScale);
      site?.style.setProperty('--film-drift-x', `${horizontalDrift}px`);
      site?.style.setProperty('--film-drift-x-reverse', `${-horizontalDrift}px`);
      site?.style.setProperty('--film-drift-y', `${verticalDrift}px`);
      site?.style.setProperty('--film-drift-y-reverse', `${-verticalDrift}px`);
      site?.style.setProperty('--film-rotation', `${((pageProgress - 0.5) * 8 + (chapterProgress - 0.5) * 4) * motionScale}deg`);
      site?.style.setProperty('--film-scale', `${1.04 + Math.sin(pageProgress * Math.PI) * 0.17 * motionScale + chapterProgress * 0.07 * motionScale}`);
      site?.style.setProperty('--film-progress', String(pageProgress));
      let currentStoryStep = 0;
      if (story && site) {
        const bounds = story.getBoundingClientRect();
        const travel = Math.max(1, bounds.height - window.innerHeight);
        const progress = Math.min(1, Math.max(0, -bounds.top / travel));
        currentStoryStep = Math.min(storyScenes.length - 1, Math.floor(progress * storyScenes.length));
        site.style.setProperty('--story-progress', String(progress));
        site.style.setProperty('--story-tone', storyScenes[currentStoryStep].tone);
        site.style.setProperty('--story-drift-x', `${Math.round((progress - 0.5) * 86)}px`);
        site.style.setProperty('--story-drift-y', `${Math.round((progress - 0.5) * 44)}px`);
        site.style.setProperty('--story-drift-reverse-x', `${Math.round((0.5 - progress) * 86)}px`);
        site.style.setProperty('--story-drift-reverse-y', `${Math.round((0.5 - progress) * 44)}px`);
        setStoryStep((current) => current === currentStoryStep ? current : currentStoryStep);
      }
      if (active) {
        setActiveChapter((current) => current === active.id ? current : active.id);
        site?.style.setProperty('--scene-tone', active.id === 'scroll-story' ? storyScenes[currentStoryStep].tone : sceneTones[active.id] ?? sceneTones.top);
      }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateChapter);
    };
    updateChapter();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const site = document.querySelector<HTMLElement>('.editorial-site');
    if (!site || !window.matchMedia('(pointer: fine) and (hover: hover)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    const onPointerMove = (event: PointerEvent) => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        site.style.setProperty('--pointer-x', `${event.clientX}px`);
        site.style.setProperty('--pointer-y', `${event.clientY}px`);
        frame = 0;
      });
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const site = document.querySelector<HTMLElement>('.editorial-site');
    if (!site) return;
    let frame = 0;
    const updateProgress = () => {
      frame = 0;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = maxScroll > 0 ? window.scrollY / maxScroll : 0;
      site.style.setProperty('--scroll-progress', String(Math.min(1, Math.max(0, progress))));
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(updateProgress); };
    updateProgress();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const activeSeries = activeItem ? projects.find((project) => project.details.includes(activeItem.id)) : undefined;
  const activeSeriesItems = activeSeries?.details.map((id) => homepageDetailVideoOverrides.get(id) ?? byId(id)) ?? [];
  const openItem = (item: MediaAsset) => {
    window.history.pushState({ ...window.history.state, portfolioModal: item.id }, '', window.location.href);
    setActiveItem(item);
  };
  const closeItem = () => {
    if (window.history.state?.portfolioModal) {
      const pageState = { ...window.history.state };
      delete pageState.portfolioModal;
      window.history.replaceState(pageState, '', window.location.href);
    }
    setActiveItem(null);
  };
  const showSeriesItem = (direction: number) => {
    if (!activeItem || activeSeriesItems.length < 2) return;
    const index = activeSeriesItems.findIndex((item) => item.id === activeItem.id);
    setActiveItem(activeSeriesItems[(index + direction + activeSeriesItems.length) % activeSeriesItems.length]);
  };

  useEffect(() => {
    const handleKeys = (event: KeyboardEvent) => {
      if (activeItem) {
        if (event.key === 'Escape') closeItem();
        if (event.key === 'ArrowLeft') showSeriesItem(-1);
        if (event.key === 'ArrowRight') showSeriesItem(1);
        return;
      }
      if (contactOpen) {
        if (event.key === 'Escape') {
          closeContact();
          return;
        }
        if (event.key === 'Tab') {
          const focusable = contactDialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)');
          if (!focusable?.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
        return;
      }
      if (event.key === 'ArrowLeft') showHeroFrame(-1);
      if (event.key === 'ArrowRight') showHeroFrame(1);
    };
    document.addEventListener('keydown', handleKeys);
    if (activeItem || contactOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeys);
      document.body.style.overflow = '';
    };
  }, [activeItem, activeSeriesItems, contactOpen]);

  useEffect(() => {
    if (!contactOpen) return;
    contactCloseRef.current?.focus();
    return () => contactTriggerRef.current?.focus();
  }, [contactOpen]);

  useEffect(() => {
    const syncPortfolioHistory = (event: PopStateEvent) => {
      const modalId = event.state?.portfolioModal;
      setActiveItem(typeof modalId === 'string' ? gallery.find((item) => item.id === modalId) ?? null : null);
    };
    window.addEventListener('popstate', syncPortfolioHistory);
    return () => window.removeEventListener('popstate', syncPortfolioHistory);
  }, []);

  return (
    <main className="editorial-site">
      <div className={`scroll-film${activeChapter === 'scroll-story' ? ' scroll-film--story' : ''}`} aria-hidden="true">
        <div className="scroll-film-collage">
          {Array.from({ length: 4 }, (_, index) => {
            const frameIndex = (Math.max(0, viewingChapters.findIndex((chapter) => chapter.id === activeChapter)) * 2 + index) % scrollFilmFrames.length;
            const frame = scrollFilmFrames[frameIndex];
            return <figure className={`scroll-film-tile scroll-film-tile--${index + 1}`} key={`${activeChapter}-${frameIndex}`}>
              <img src={frame.src} alt="" loading={activeChapter === 'top' ? 'eager' : 'lazy'} />
              <span>0{String(frameIndex + 1).padStart(2, '0')} / 0{String(scrollFilmFrames.length).padStart(2, '0')}</span>
            </figure>;
          })}
        </div>
        <div className="scroll-film-shade" />
      </div>
      <div className="scroll-progress" aria-hidden="true" />
      <nav className="chapter-rail" aria-label="Главы портфолио">
        <span className="chapter-rail-title">ПРОСМОТР</span>
        {viewingChapters.map((chapter, index) => <a key={chapter.id} href={`#${chapter.id}`} className={activeChapter === chapter.id ? 'is-active' : ''} aria-current={activeChapter === chapter.id ? 'location' : undefined}>
          <span className="chapter-rail-number">0{index + 1}</span><span className="chapter-rail-label">{chapter.label}</span>
        </a>)}
      </nav>
      <header className="editorial-header">
        <a className="brand-mark" href="#top" aria-label="Денис Пастернак — наверх">DP<span>®</span></a>
        <nav id="main-navigation" className={`main-nav ${menuOpen ? 'main-nav--open' : ''}`} aria-label="Навигация">
          <a href="#work" onClick={() => setMenuOpen(false)}>Работы</a>
          <a href="#formats" onClick={() => setMenuOpen(false)}>Услуги</a>
          <a href="#expert-videos" onClick={() => setMenuOpen(false)}>Экспертные ролики</a>
          <a href="#learning-materials" onClick={() => setMenuOpen(false)}>Гайды</a>
          <a href="#about" onClick={() => setMenuOpen(false)}>Обо мне</a>
        </nav>
        <div className="header-meta">
          <div className="header-note">КРЕАТИВНЫЙ ПРОДЮСЕР<br />ПОРТФОЛИО · 2026</div>
          <nav className="header-socials" aria-label="Социальные сети и контакты">
            <a href="https://www.instagram.com/deniskg" target="_blank" rel="noreferrer" aria-label="Instagram Дениса Пастернака" title="Instagram">
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.7" cy="6.5" r=".8" className="icon-fill" /></svg>
            </a>
            <a href="https://t.me/deniskg" target="_blank" rel="noreferrer" aria-label="Telegram Дениса Пастернака" title="Telegram">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 4-3.2 16-6.2-4.5-3.3 3.1.5-5.1L18 7.1 7 13l-4-1.6L21 4Z" /></svg>
            </a>
            <a href="https://wa.me/77081087904" target="_blank" rel="noreferrer" aria-label="WhatsApp Дениса Пастернака" title="WhatsApp">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.3 11.7a8.3 8.3 0 0 1-12.2 7.4L3.7 20l.9-4.2a8.3 8.3 0 1 1 15.7-4.1Z" /><path d="M8.4 8.1c.2-.5.5-.5.8-.5h.5c.2 0 .4.1.5.4l.8 1.9c.1.2.1.4-.1.6l-.6.7c-.2.2-.2.4 0 .6.6 1 1.4 1.7 2.4 2.2.2.1.4.1.6-.1l.8-.9c.2-.2.4-.2.6-.1l1.8.9c.2.1.3.3.3.5 0 .4-.2 1-.6 1.3-.4.4-1 .6-1.6.6-1 0-2.4-.5-4-1.9-1.4-1.2-2.3-2.7-2.6-3.8-.3-1 0-1.9.4-2.4Z" /></svg>
            </a>
            <a href="https://mail.google.com/mail/?view=cm&fs=1&to=arhandez%40gmail.com" target="_blank" rel="noreferrer" aria-label="Написать на email arhandez@gmail.com" title="Email">
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
            </a>
          </nav>
        </div>
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} aria-expanded={menuOpen} aria-controls="main-navigation">
          {menuOpen ? 'Закрыть' : 'Меню'}
        </button>
      </header>

      <section className="editorial-hero" id="top">
        <div className="hero-copy">
          <p className="eyebrow">АВТОР <span>·</span> КРЕАТИВНЫЙ ПРОДЮСЕР</p>
          <h1 className="hero-title">Денис<br /><span>Пастернак</span></h1>
          <div className="hero-bottomline" data-reveal>
            <p>AI-реклама, ролики и визуальные кампании для брендов, агентств и авторов — от идеи до готового креатива.</p>
            <a href="#work" className="round-link" aria-label="Перейти к работам">↓</a>
          </div>
          <nav className="hero-actions" aria-label="Действия">
            <a className="hero-cta-primary" href="#work">Смотреть работы <span>↓</span></a>
            <button className="hero-cta-secondary" type="button" onClick={(event) => openContact(event.currentTarget)}>Обсудить задачу <span>↗</span></button>
          </nav>
        </div>
        <div className="hero-image-wrap" data-reveal onTouchStart={(event) => { heroTouchStart.current = event.touches[0]?.clientX ?? null; }} onTouchEnd={(event) => {
          if (heroTouchStart.current === null) return;
          const delta = event.changedTouches[0]?.clientX - heroTouchStart.current;
          heroTouchStart.current = null;
          if (typeof delta === 'number' && Math.abs(delta) > 45) showHeroFrame(delta < 0 ? 1 : -1);
        }}>
          <div className="hero-image-trigger" aria-hidden="true">
            <AssetImage key={heroItem.id} item={heroItem} eager className={`hero-image${heroItem.id === 'hero-video-03' ? ' hero-image--crop-edge' : ''}`} />
          </div>
          <button className="hero-arrow hero-arrow--prev" onClick={() => showHeroFrame(-1)} aria-label="Предыдущее видео">←</button>
          <button className="hero-arrow hero-arrow--next" onClick={() => showHeroFrame(1)} aria-label="Следующее видео">→</button>
          <span className="hero-image-caption"><span>{heroCaptions[heroIndex]}</span><span className="hero-image-caption-meta"><span>{String(heroIndex + 1).padStart(2, '0')} / {String(heroFrames.length).padStart(2, '0')}</span><span className="hero-frame-progress" aria-label="Кадры обложки">{heroFrames.map((frame, index) => <button key={frame.id} type="button" aria-label={`Показать кадр ${index + 1}: ${heroCaptions[index]}`} aria-pressed={heroIndex === index} className={heroIndex === index ? 'is-active' : ''} onClick={() => setHeroIndex(index)} />)}</span></span></span>
        </div>
        <div className="hero-index"><span>ИЗБРАННЫЕ РАБОТЫ</span><span>↓ ПРОКРУТИТЕ ВНИЗ</span></div>
      </section>

      <section className="story-run" id="scroll-story" aria-labelledby="story-title">
        <div className="story-stage">
          <div className="story-collage" aria-hidden="true">
            {projects.map((project, index) => <figure className={`story-tile story-tile--${index + 1}`} key={project.number}>
              <img src={project.cover.poster ?? project.cover.src} alt="" loading="lazy" />
              <span>{project.number} / 04</span>
            </figure>)}
          </div>
          <div className="story-vignette" aria-hidden="true" />
          <div className="story-copy" key={storyStep}>
            <p className="story-eyebrow">{storyScenes[storyStep].eyebrow}</p>
            <h2 id="story-title">{storyScenes[storyStep].title.split('\n').map((line) => <span key={line}>{line}</span>)}</h2>
            <p className="story-description">{storyScenes[storyStep].description}</p>
          </div>
          <div className="story-footer">
            <div className="story-step-track" aria-label={`Этап ${storyStep + 1} из ${storyScenes.length}`}>
              {storyScenes.map((scene, index) => <span key={scene.eyebrow} className={index === storyStep ? 'is-active' : ''} />)}
            </div>
            <span className="story-step-count">0{storyStep + 1} <i>/</i> 0{storyScenes.length}</span>
            <a href="#work">ИЗБРАННЫЕ РАБОТЫ <span aria-hidden="true">↓</span></a>
          </div>
        </div>
      </section>

      <section className="work-section" id="work">
        <div className="section-heading" data-reveal>
          <div><p className="eyebrow">ИЗБРАННЫЕ ПРОЕКТЫ</p><h2>От идеи<br />к образу.</h2></div>
          <p className="section-intro">Направление, персонаж, кадр — собранные в цельную визуальную историю.</p>
        </div>

        <div className="project-list">
          {projects.map((project, index) => (
            <Fragment key={project.number}>
            <article className={`project-row project-row--${index + 1}`} data-reveal>
              <button className="project-cover" onClick={() => openItem(project.cover)} aria-label={`Открыть проект ${project.title.replace('\n', ' ')}`}>
                {project.cover.type === 'video' ? (
                  <AutoplayVideo item={project.cover} className="project-image" />
                ) : <AssetImage item={project.cover} eager={index < 2} className="project-image" />}
                <span className="project-cover-index">{project.number}</span>
                <span className="project-cover-arrow">↗</span>
                <span className="project-cover-cta">СМОТРЕТЬ ИСТОРИЮ</span>
              </button>
              <div className="project-copy">
                <p className="project-eyebrow">{project.eyebrow}</p>
                <h3>{project.title.split('\n').map((line) => <span key={line}>{line}</span>)}</h3>
                <p className="project-description">{project.description}</p>
                <dl className="project-facts">
                  <div><dt>ФОРМАТ</dt><dd>{project.format}</dd></div>
                  <div><dt>В ФОКУСЕ</dt><dd>{project.focus}</dd></div>
                </dl>
                <div className="project-thumbnails">
                  {project.details.map((id) => {
                    const item = homepageDetailVideoOverrides.get(id) ?? byId(id);
                    if (project.number === '02') {
                      return <div className="project-thumb-static" key={id}><AssetImage item={item} className="project-thumb" /></div>;
                    }
                    return <button key={id} onClick={() => openItem(item)} aria-label={`Открыть ${item.title}`}><AssetImage item={item} className="project-thumb" />{item.type === 'video' && <span className="project-thumb-video-mark">▶</span>}</button>;
                  })}
                </div>
                <button className="text-link" onClick={() => openItem(project.cover)}>Смотреть проект <span>↗</span></button>
              </div>
            </article>
            </Fragment>
          ))}
        </div>
      </section>

      <section className="offer-section" id="formats" aria-labelledby="offer-title">
        <div className="offer-heading" data-reveal>
          <div><p className="eyebrow">ЧЕМ МОГУ ПОМОЧЬ</p><h2 id="offer-title">Визуал под<br />вашу задачу<span>.</span></h2></div>
          <p>От одного выразительного ролика до набора креативов для кампании. Формат и объём подбираются под цель, аудиторию и площадку.</p>
        </div>
        <div className="offer-grid">
          <article className="offer-card" data-reveal><span className="offer-number">01</span><p className="eyebrow">БРЕНДАМ И БИЗНЕСУ</p><h3>Реклама<br />и кампании</h3><p>Продуктовые ролики, визуальные концепции и рекламные материалы для запуска или обновления кампании.</p><ul><li>AI-видеоролик</li><li>Ключевые кадры кампании</li><li>Адаптации под площадки</li></ul><button className="text-link" type="button" onClick={(event) => openContact(event.currentTarget, 'Реклама и кампания')}>Обсудить кампанию <span>↗</span></button></article>
          <article className="offer-card offer-card--dark" data-reveal><span className="offer-number">02</span><p className="eyebrow">АГЕНТСТВАМ И КОМАНДАМ</p><h3>Креативы<br />для тестов</h3><p>Несколько визуальных направлений и вариантов подачи, чтобы команде было из чего выбирать и что тестировать.</p><ul><li>Варианты концепции</li><li>Статика и короткое видео</li><li>Форматы для digital-рекламы</li></ul><button className="text-link" type="button" onClick={(event) => openContact(event.currentTarget, 'Креативы для рекламных тестов')}>Обсудить объём <span>↗</span></button></article>
          <article className="offer-card" data-reveal><span className="offer-number">03</span><p className="eyebrow">АВТОРАМ И КРЕАТОРАМ</p><h3>Образ<br />и история</h3><p>Выразительная визуальная подача для личного бренда, Reels, Shorts и контента, который сложно снять обычным способом.</p><ul><li>Визуальная концепция</li><li>Короткие сюжетные ролики</li><li>Персонажи и спецэффекты</li></ul><button className="text-link" type="button" onClick={(event) => openContact(event.currentTarget, 'Контент для автора или креатора')}>Обсудить идею <span>↗</span></button></article>
          <article className="offer-card" data-reveal><span className="offer-number">04</span><p className="eyebrow">СТАРТАПАМ И DIGITAL-ПРОДУКТАМ</p><h3>Контент<br />для запуска</h3><p>Рекламные визуалы и вариации креативов для презентации продукта, запуска и проверки гипотез.</p><ul><li>Продуктовые сцены</li><li>Performance-варианты</li><li>Вертикальные видео</li></ul><button className="text-link" type="button" onClick={(event) => openContact(event.currentTarget, 'Креативы для запуска продукта')}>Обсудить запуск <span>↗</span></button></article>
        </div>
        <p className="offer-note">Состав материалов, сроки и стоимость определяются после короткого обсуждения задачи.</p>
      </section>

      <section className="process-section" id="process" aria-labelledby="process-title">
        <div className="process-heading" data-reveal><p className="eyebrow">ПОНЯТНЫЙ ПРОЦЕСС</p><h2 id="process-title">От задачи<br />до готового материала<span>.</span></h2><p>Работа начинается с цели и контекста, а не с генерации ради генерации.</p></div>
        <ol className="process-grid">
          <li data-reveal><span>01</span><h3>Задача</h3><p>Уточняем аудиторию, площадку, продукт и то, что должен сделать контент.</p></li>
          <li data-reveal><span>02</span><h3>Направление</h3><p>Собираю идею, визуальный стиль и последовательность сцен для согласования.</p></li>
          <li data-reveal><span>03</span><h3>Производство</h3><p>Создаю визуалы и видео, затем отбираю и дорабатываю материал под задачу.</p></li>
          <li data-reveal><span>04</span><h3>Передача</h3><p>Подготавливаю согласованные файлы и варианты форматов для выбранных площадок.</p></li>
        </ol>
      </section>

      <section className="expert-section" id="expert-videos" aria-labelledby="expert-title">
        <div className="expert-heading" data-reveal>
          <div><p className="eyebrow">ЭКСПЕРТНЫЙ КОНТЕНТ</p><h2 id="expert-title">Разбираю AI<br />на практике<span>.</span></h2></div>
          <p>Ролики о нейросетях, сценариях и визуальном производстве — от рабочего процесса до готовых концептов.</p>
        </div>
        <div className="expert-video-grid">{expertClips.map((clip, index) => <ExpertVideoCard key={clip.id} clip={clip} index={index} />)}</div>
      </section>

      <section className="learning-section" id="learning-materials" aria-labelledby="learning-title">
        <div className="learning-heading" data-reveal>
          <div><p className="eyebrow">БИБЛИОТЕКА · 09 МАТЕРИАЛОВ</p><h2 id="learning-title">Обучающие<br />материалы<span>.</span></h2></div>
          <p>Короткие гайды по AI-инструментам, генерации изображений и созданию видео. Открой нужный — и сразу переходи к практике.</p>
        </div>
        <div className="learning-grid">
          {learningGuides.map((guide, index) => <details className="learning-card" key={guide.href} data-reveal>
            <summary className="learning-card-summary">
              <span className="learning-card-number">{String(index + 1).padStart(2, '0')}</span>
              <span className="learning-card-summary-copy"><span className="learning-card-category">{guide.category}</span><span className="learning-card-title">{guide.title}</span></span>
              <span className="learning-card-toggle" aria-hidden="true">+</span>
            </summary>
            <div className="learning-card-expanded">
              <a className="learning-cover" href={guide.href} target="_blank" rel="noreferrer" aria-label={`Открыть гайд: ${guide.title}`}><img src={guide.cover} alt="" loading="lazy" /><b aria-hidden="true">↗</b></a>
              <p>{guide.description}</p>
              <a className="learning-open-link" href={guide.href} target="_blank" rel="noreferrer">ОТКРЫТЬ ГАЙД <span aria-hidden="true">↗</span></a>
            </div>
          </details>)}
        </div>
        <div className="learning-footer"><p>Инструкции, примеры и готовые приёмы — к материалам можно вернуться в любой момент.</p><a href="https://denis-ai-generation-guide.arhandez.chatgpt.site/#library" target="_blank" rel="noreferrer">ВСЯ БИБЛИОТЕКА <span aria-hidden="true">↗</span></a></div>
      </section>

      <section className="faq-section" id="faq" aria-labelledby="faq-title">
        <div className="faq-heading" data-reveal><p className="eyebrow">ЧАСТЫЕ ВОПРОСЫ</p><h2 id="faq-title">Перед стартом<span>.</span></h2><p>Если у тебя уже есть конкретная идея или только задача — можно начать с короткого разговора.</p><button className="text-link" type="button" onClick={(event) => openContact(event.currentTarget)}>Задать свой вопрос <span>↗</span></button></div>
        <div className="faq-list">
          <details data-reveal><summary>Что можно заказать?</summary><p>Короткое рекламное видео, статичные креативы, визуальную кампанию, персонажа, серию изображений или концепцию для Reels и Shorts.</p></details>
          <details data-reveal><summary>Нужно ли приходить с готовой идеей?</summary><p>Нет. Можно прийти с продуктом, аудиторией и задачей — визуальное направление и сценарную идею можно разработать в процессе.</p></details>
          <details data-reveal><summary>Для каких площадок подходят материалы?</summary><p>Форматы подбираются под размещение: например, вертикальные ролики для Reels и Shorts или креативы для digital-рекламы. Площадки и размеры обсуждаем заранее.</p></details>
          <details data-reveal><summary>Можно ли сначала проверить одну идею?</summary><p>Да, можно обсудить небольшой пилотный материал и на его основе определить дальнейший объём работ.</p></details>
          <details data-reveal><summary>От чего зависят сроки и стоимость?</summary><p>От сложности концепции, количества сцен и материалов, длительности ролика и числа форматов. После обсуждения задачи можно определить объём и подготовить оценку.</p></details>
          <details data-reveal><summary>Всегда ли AI заменяет обычную съёмку?</summary><p>Не всегда. Генеративный подход особенно полезен для сложных сцен, персонажей, быстрых визуальных поисков и гибких вариантов. Подход выбирается под задачу и нужный результат.</p></details>
        </div>
      </section>

      <section className="about-section" id="about">
        <p className="eyebrow">ОБО МНЕ</p>
        <div className="about-layout" data-reveal>
          <h2>Визуальные<br />решения с<br /><span>характером.</span></h2>
          <div className="about-copy">
            <p>Создаю коммерческий AI-контент для брендов, рекламных агентств, digital-продуктов и авторов. Помогаю быстрее проверять визуальные гипотезы и выпускать рекламу с меньшей зависимостью от традиционного продакшена.</p>
            <p>Делаю короткие ролики, статичные креативы и визуальные кампании для Reels, Shorts и performance-рекламы. Работаю с генеративными инструментами, виртуальными моделями, персонажами и предметными сценами — от концепции до готовых материалов.</p>
            <div className="collaboration-cta"><p>Нужна визуальная концепция, серия кадров или ролик?</p><button className="text-link" type="button" onClick={(event) => openContact(event.currentTarget)}>Обсудить задачу <span>↗</span></button></div>
          </div>
        </div>
      </section>

      <footer className="editorial-footer">
        <a className="brand-mark" href="#top">DP<span>®</span></a>
        <span>ДЕНИС ПАСТЕРНАК · КРЕАТИВНЫЙ ПРОДЮСЕР</span>
        <a href="#top">НАВЕРХ ↑</a>
      </footer>

      <a className="floating-top-link" href="#top" aria-label="Быстро подняться наверх">↑</a>

      {contactOpen && <div className="contact-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeContact(); }}>
        <section ref={contactDialogRef} className="contact-dialog" role="dialog" aria-modal="true" aria-labelledby="contact-dialog-title" aria-describedby="contact-dialog-description contact-brief-description">
          <button ref={contactCloseRef} className="contact-dialog-close" type="button" onClick={closeContact} aria-label="Закрыть выбор контакта">×</button>
          <p className="eyebrow">СВЯЗАТЬСЯ</p>
          <h2 id="contact-dialog-title">Обсудить задачу</h2>
          <p id="contact-dialog-description">Выберите удобный способ связи. Можно начать с черновой идеи — помогу определить формат и следующий шаг.</p>
          {contactTopic && <div className="contact-dialog-topic"><span>НАПРАВЛЕНИЕ</span><strong>{contactTopic}</strong></div>}
          <div className="contact-brief">
            <p className="eyebrow">ЧТО НАПИСАТЬ</p>
            <p id="contact-brief-description">Чтобы быстрее оценить задачу, добавьте в сообщение:</p>
            <ul><li>что продвигаем</li><li>какая цель у материала</li><li>формат и площадка, если уже известны</li><li>желаемый срок</li></ul>
          </div>
          <nav className="contact-options" aria-label="Способы связи">
            <a href="https://www.instagram.com/deniskg" target="_blank" rel="noreferrer"><span className="contact-option-mark">IG</span><span>Instagram</span><span aria-hidden="true">↗</span></a>
            <a href="https://t.me/deniskg" target="_blank" rel="noreferrer"><span className="contact-option-mark">TG</span><span>Telegram</span><span aria-hidden="true">↗</span></a>
            <a href="https://wa.me/77081087904" target="_blank" rel="noreferrer"><span className="contact-option-mark">WA</span><span>WhatsApp</span><span aria-hidden="true">↗</span></a>
            <a href="mailto:arhandez@gmail.com?subject=Обсуждение%20проекта"><span className="contact-option-mark">@</span><span>Email</span><span aria-hidden="true">↗</span></a>
          </nav>
        </section>
      </div>}

      {activeItem && <div className="lightbox" role="dialog" aria-modal="true" aria-label={activeItem.title} onClick={closeItem}>
        <button className="lightbox-close" type="button" onClick={(event) => { event.stopPropagation(); closeItem(); }} aria-label="Закрыть">×</button>
        {activeSeriesItems.length > 1 && <>
          <button className="lightbox-series-arrow lightbox-series-arrow--prev" onClick={(event) => { event.stopPropagation(); showSeriesItem(-1); }} aria-label="Предыдущая работа в серии">←</button>
          <button className="lightbox-series-arrow lightbox-series-arrow--next" onClick={(event) => { event.stopPropagation(); showSeriesItem(1); }} aria-label="Следующая работа в серии">→</button>
        </>}
        <div className="lightbox-content" onClick={(event) => event.stopPropagation()}>
          {activeItem.type === 'video' ? <video key={activeItem.id} ref={(video) => { if (video) video.volume = 0.5; }} src={activeItem.src} poster={activeItem.poster} controls autoPlay loop playsInline /> : <img src={activeItem.src} alt={activeItem.alt} />}
          <div className="lightbox-caption"><span>{activeItem.title}<small>{contextForItem(activeItem.id)}{activeSeriesItems.length > 1 ? ` · ${activeSeriesItems.findIndex((item) => item.id === activeItem.id) + 1} / ${activeSeriesItems.length} в серии` : ''}</small></span><span>{activeItem.description}</span></div>
        </div>
      </div>}
    </main>
  );
}

export default App;
